import { ArrowLeft, ArrowRight, FileText } from "lucide-react";
import BackControls from "../components/BackControls";
import { lazy, Suspense, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "@/redux/hooks";
import type { Gender } from "../data/sharedTypes";
import StepHeader from "../StepHeader";

// Lazy-loaded so react-pdf / pdfjs-dist (which touch browser-only globals)
// are never evaluated during the SSR build or on the server.
const PdfModal = lazy(() => import("../components/PdfModal"));

interface Props {
    gender: Gender;
    universityNameKey: string;
    onBack: () => void;
}

interface DocItem {
    key: "application" | "acceptance";
    labelKey: string;
    file: string;
}

/**
 * PAAET — minimum-rates documents step.
 *
 * Temporary replacement for the PAAET college → specialty → scores flow:
 * after the gender step, the user picks one of two official PAAET PDFs
 * (minimum application rates / minimum acceptance rates) which open in a
 * modal viewer. The original PAAET calculation flow is left intact in the
 * codebase and simply bypassed (see DreamUniversitySection).
 */
export default function PaaetDocs({ gender, universityNameKey, onBack }: Props) {
    const { t } = useTranslation();
    const { locale } = useAppSelector((state) => state);
    const [openDoc, setOpenDoc] = useState<DocItem | null>(null);

    const docs: DocItem[] = [
        {
            key: "application",
            labelKey: "dream_university_paaet_doc_application",
            file: `/pdfs/paaet/${gender}-min-application.pdf`,
        },
        {
            key: "acceptance",
            labelKey: "dream_university_paaet_doc_acceptance",
            file: `/pdfs/paaet/${gender}-min-acceptance.pdf`,
        },
    ];

    return (
        <div className="flex flex-col gap-y-6">
            <BackControls onBack={onBack} />

            <StepHeader
                title={t("dream_university_paaet_docs_title")}
                subtitle={`${t(universityNameKey)} · ${t(`dream_university_gender_${gender}`)}`}
            />

            <div className="w-full max-w-md mx-auto flex flex-col gap-3">
                {docs.map((doc) => (
                    <button
                        key={doc.key}
                        onClick={() => setOpenDoc(doc)}
                        className="w-full flex items-center gap-4 p-5 bg-white border-2 border-gray-200 rounded-2xl hover:border-theme-500 hover:shadow-lg transition-all duration-200 group text-start"
                    >
                        <div className="w-12 h-12 bg-theme-100 rounded-xl flex items-center justify-center group-hover:bg-theme-200 transition-colors shrink-0">
                            <FileText className="w-6 h-6 text-theme-600" />
                        </div>
                        <span className="text-base font-semibold text-gray-900 leading-snug">
                            {t(doc.labelKey)}
                        </span>
                    </button>
                ))}
            </div>

            {openDoc && (
                <Suspense fallback={null}>
                    <PdfModal
                        fileUrl={openDoc.file}
                        title={t(openDoc.labelKey)}
                        onClose={() => setOpenDoc(null)}
                    />
                </Suspense>
            )}
        </div>
    );
}
